/**
 * Ledger Connect Form
 *
 * Form to connect a Ledger hardware wallet
 */

import { useState } from "react";
import { ArrowLeft, HardDrive, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useBrowserWallet, useBrowserWalletStore } from "@nullaone/wallet-react";
import { DERIVATION_PATHS } from "@nullaone/wallet-core";

type ConnectStep = "instructions" | "connecting" | "success";

interface LedgerConnectState {
  step: ConnectStep;
  path: string;
  address: string | null;
}

export function LedgerConnectForm() {
  const { connectLedger, connecting, closeModal } = useBrowserWallet();
  const { setModalStep } = useBrowserWalletStore();

  const [state, setState] = useState<LedgerConnectState>({
    step: "instructions",
    path: DERIVATION_PATHS.DEFAULT,
    address: null,
  });

  const handleConnect = async () => {
    setState((prev) => ({ ...prev, step: "connecting" }));

    try {
      await connectLedger(state.path);
      closeModal();
    } catch {
      setState((prev) => ({ ...prev, step: "instructions" }));
    }
  };

  return (
    <div className="space-y-6 py-4">
      <Button
        variant="ghost"
        size="sm"
        className="gap-2"
        onClick={() => setModalStep("select")}
        disabled={state.step === "connecting" || connecting}
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </Button>

      {state.step === "instructions" && (
        <div className="space-y-6">
          {/* Instructions */}
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                1
              </div>
              <div className="space-y-1">
                <p className="font-medium">Connect your Ledger</p>
                <p className="text-sm text-muted-foreground">
                  Plug in your Ledger device and unlock it with your PIN
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                2
              </div>
              <div className="space-y-1">
                <p className="font-medium">Open Solana App</p>
                <p className="text-sm text-muted-foreground">
                  Navigate to the Solana app on your Ledger and open it
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                3
              </div>
              <div className="space-y-1">
                <p className="font-medium">Click Connect</p>
                <p className="text-sm text-muted-foreground">
                  Click the button below and select your Ledger device
                </p>
              </div>
            </div>
          </div>

          {/* Derivation path selector */}
          <div className="space-y-2">
            <Label>Derivation Path</Label>
            <Select
              value={state.path}
              onValueChange={(value) =>
                setState((prev) => ({ ...prev, path: value }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DERIVATION_PATHS.DEFAULT}>
                  Default ({DERIVATION_PATHS.DEFAULT})
                </SelectItem>
                <SelectItem value={DERIVATION_PATHS.LEDGER_LIVE}>
                  Ledger Live ({DERIVATION_PATHS.LEDGER_LIVE})
                </SelectItem>
                <SelectItem value={DERIVATION_PATHS.ACCOUNT_1}>
                  Account 2 ({DERIVATION_PATHS.ACCOUNT_1})
                </SelectItem>
                <SelectItem value={DERIVATION_PATHS.ACCOUNT_2}>
                  Account 3 ({DERIVATION_PATHS.ACCOUNT_2})
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Most users should use the default path
            </p>
          </div>

          <Button
            className="w-full"
            size="lg"
            onClick={handleConnect}
            disabled={connecting}
          >
            <HardDrive className="mr-2 h-4 w-4" />
            Connect Ledger
          </Button>
        </div>
      )}

      {state.step === "connecting" && (
        <div className="flex flex-col items-center gap-6 py-8">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <div className="text-center space-y-2">
            <p className="font-medium">Connecting to Ledger...</p>
            <p className="text-sm text-muted-foreground">
              Select your device in the browser popup and confirm on your Ledger
            </p>
          </div>
        </div>
      )}

      {/* Warning */}
      <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-amber-600">
              Enable Blind Signing
            </p>
            <p className="text-xs text-amber-600/80">
              Some transactions may require blind signing to be enabled in your
              Ledger Solana app settings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
